import React, { useCallback, useEffect, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
    ButtonGroup,
    ModalControl,
    ModalProvider,
} from '@paygreen/paygreen-ui';
import { EditButton } from '../../../components';
import UserCompanyBillingAddressModal from './UserCompanyBillingAddressModal';
import UserDisplayBillingData from './UserDisplayBillingData';
import { useAPIError, useUser } from '../../../context';
import { getAccountAddress } from '../service';

const UserCompanyBillingAddress = ({ t, account, setHasAdressFilled }) => {
    const { addError } = useAPIError();
    const { user } = useUser();
    const role = user?.role === 'ASSOCIATION' ? 'ASSOCIATION' : 'SHOP';

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    const [billingAddressData, setBillingAddressData] = useState();
    const [isLoading, setIsLoading] = useState(true);

    /**
     * GET request to API to get user's billing address
     * @returns {object} - object with all billing address data
     */
    const getAccountBillingAddress = useCallback(
        (account) => {
            getAccountAddress(account)
                .then((res) => {
                    if (isMounted.current) {
                        setBillingAddressData(res.data);
                        setHasAdressFilled && setHasAdressFilled(true);
                    }
                })
                .catch((error) => {
                    const { detail, status } = error?.response?.data;

                    // This is normal, it's possible not having billingAddress
                    if (isMounted.current && status !== 404) {
                        addError(detail, status);
                    }
                })
                .finally(() => isMounted.current && setIsLoading(false));
        },
        [addError, setHasAdressFilled],
    );

    useEffect(() => {
        isMounted.current = true;

        if (account) {
            getAccountBillingAddress(account);
        }

        return () => {
            isMounted.current = false;
        };
    }, [getAccountBillingAddress, account]);

    return (
        <UserDisplayBillingData
            data={billingAddressData}
            isLoading={isLoading}
            role={role}
        >
            <ModalProvider>
                <ButtonGroup>
                    <ModalControl hasOnClickControl={true}>
                        <EditButton buttonSize="sm" buttonStyle="line" dataCy="adress-edit">
                            {billingAddressData
                                ? t('form.button.edit')
                                : t('form.button.add')}
                        </EditButton>
                    </ModalControl>
                </ButtonGroup>

                <ModalControl>
                    <UserCompanyBillingAddressModal
                        account={account}
                        data={billingAddressData}
                        setIsLoading={setIsLoading}
                        isLoading={isLoading}
                        setHasAdressFilled={setHasAdressFilled}
                        setBillingAddressData={setBillingAddressData}
                        role={role}
                    />
                </ModalControl>
            </ModalProvider>
        </UserDisplayBillingData>
    );
};

UserCompanyBillingAddress.propTypes = {
    account: PropTypes.string,
    setHasAdressFilled: PropTypes.func,
};

UserCompanyBillingAddress.defaultProps = {
    account: '',
    setHasAdressFilled: null,
};

export default withTranslation('common')(UserCompanyBillingAddress);
